import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const ENTRANCE_DURATION_SECONDS = 0.3;
const START_SCALE = 0.92;

/**
 * Scales its children from 92% to 100% over ~300ms once `active` is true,
 * giving the generated model a subtle entrance. Snaps to full size under
 * prefers-reduced-motion. Scale-only by design: Result builds its own
 * materials, so animating opacity there would be invasive.
 *
 * The Canvas tree remounts per result (SceneErrorBoundary is keyed on the mesh
 * URL), so the animation naturally replays for each new model.
 */
function ModelEntrance({
  active,
  children,
}: {
  active: boolean;
  children: React.ReactNode;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const progressRef = useRef(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  useFrame((_, delta) => {
    const group = groupRef.current;
    if (!group) return;
    if (!active) {
      progressRef.current = 0;
      group.scale.setScalar(START_SCALE);
      return;
    }
    if (prefersReducedMotion || progressRef.current >= 1) {
      group.scale.setScalar(1);
      return;
    }
    progressRef.current = Math.min(
      1,
      progressRef.current + delta / ENTRANCE_DURATION_SECONDS,
    );
    const eased = 1 - Math.pow(1 - progressRef.current, 3);
    group.scale.setScalar(START_SCALE + (1 - START_SCALE) * eased);
  });

  return <group ref={groupRef}>{children}</group>;
}

export default ModelEntrance;
